import React, { useEffect, useState } from 'react';
import { useAINews } from '../hooks/useAINews';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Rocket, TrendingUp, Zap } from 'lucide-react';
export function AINewsNarrator() {
  const {
    news,
    loading
  } = useAINews();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [displayedCategory, setDisplayedCategory] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const currentNews = news[currentIndex];
  // Category icon mapping
  const getCategoryIcon = (category: string) => {
    const lowerCategory = category?.toLowerCase() || '';
    if (lowerCategory.includes('launch') || lowerCategory.includes('release')) return <Rocket size={12} data-id="element-8" />;
    if (lowerCategory.includes('trend') || lowerCategory.includes('popular')) return <TrendingUp size={12} data-id="element-9" />;
    if (lowerCategory.includes('update') || lowerCategory.includes('new')) return <Zap size={12} data-id="element-10" />;
    return <Sparkles size={12} data-id="element-11" />;
  };
  // Category color mapping
  const getCategoryColor = (category: string) => {
    const lowerCategory = category?.toLowerCase() || '';
    if (lowerCategory.includes('launch') || lowerCategory.includes('release')) return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
    if (lowerCategory.includes('trend') || lowerCategory.includes('popular')) return 'text-green-400 bg-green-500/10 border-green-500/20';
    if (lowerCategory.includes('update') || lowerCategory.includes('new')) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20';
  };
  // Typing animation effect
  useEffect(() => {
    if (!currentNews) return;
    setDisplayedCategory('');
    setDisplayedText('');
    setIsTyping(true);
    let currentChar = 0;
    const category = currentNews.category || '';
    const title = currentNews.title || '';
    const fullText = category + title;
    const typingInterval = setInterval(() => {
      if (currentChar < category.length) {
        setDisplayedCategory(category.slice(0, currentChar + 1));
      } else if (currentChar < fullText.length) {
        setDisplayedText(title.slice(0, currentChar - category.length + 1));
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
      currentChar++;
    }, 30);
    return () => clearInterval(typingInterval);
  }, [currentIndex, currentNews]);
  // Auto-rotate news items
  useEffect(() => {
    if (loading || news.length === 0) return;
    const rotateInterval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % news.length);
    }, 6000);
    return () => clearInterval(rotateInterval);
  }, [news, loading]);
  if (loading || !currentNews) return null;
  return <div className="flex items-center gap-3 font-mono text-sm min-w-0" data-id="element-12">
      {/* Live indicator */}
      <div className="flex items-center gap-2 shrink-0" data-id="element-13">
        <span className="relative flex h-2 w-2" data-id="element-14">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" data-id="element-15"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" data-id="element-16"></span>
        </span>
        <span className="text-xs font-bold tracking-wider uppercase text-cyan-400" data-id="element-17">
          Live AI News
        </span>
      </div>

      <span className="text-white/20 shrink-0" data-id="element-18">|</span>

      {/* News content */}
      <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden" data-id="element-19">
        <AnimatePresence mode="wait" data-id="element-20">
          <motion.div key={currentIndex} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="flex items-center gap-2 whitespace-nowrap" data-id="element-21">
            {/* Category badge */}
            {displayedCategory && <span className={`px-2 py-0.5 rounded-md text-xs font-bold border flex items-center gap-1 shrink-0 ${getCategoryColor(currentNews.category)}`} data-id="element-22">
                {getCategoryIcon(currentNews.category)}
                {displayedCategory}
              </span>}

            {/* News title */}
            <span className="text-white truncate" data-id="element-23">{displayedText}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Counter */}
      <span className="text-xs text-gray-500 shrink-0" data-id="element-24">
        {currentIndex + 1}/{news.length}
      </span>
    </div>;
}